'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import ManageAuthComponent from '@/components/manage-auth-component';
import PageLoadingState from '@/components/page-loading-state';
import { buildWorkspaceAwarePath, getResolvedWorkspaceId } from '@/lib/active-workspace';
import {
  armCompletionScreen,
  clearCompletionScreenDisplayState,
  clearCompletionScreenSeen,
} from '@/lib/completion-screen-state';
import {
  clearFlowState,
  getFlowState,
  isPersistedSystemId,
  isTemporarySystemId,
  updateFlowState,
} from '@/lib/connector-flow-state';
import { pushWithMinimumDelay } from '@/lib/navigation';
import {
  AuthConfig,
  createAuthConfig,
  getAuthConfig,
  updateAuthConfig,
} from '@/services/auth-config-service';
import { batchCreateActions, type GeneratedAction } from '@/services/action-service';
import { useGenerateSystemSummary } from '@/services/hooks/use-generate-system-summary';
import { useSetSystemStatus, useUpdateSystem } from '@/services/hooks/use-systems';
import { revalidateOnboardingProgress } from '@/services/onboarding-service';
import { createSystem } from '@/services/system-service';

type AddConnectorAuthenticationStepProps = {
  systemId: string;
  draftConfig: Partial<AuthConfig> | null;
  onDraftChange: (config: Partial<AuthConfig> | null) => void;
  onCompleted: () => void;
  onRequestClose: () => void;
};

function hasMeaningfulAuthDraft(config: Partial<AuthConfig> | null): boolean {
  if (!config) return false;

  switch (config.auth_type) {
    case 'api_key':
      return Boolean(config.api_key?.name?.trim() || config.api_key?.value?.trim());
    case 'basic_auth':
      return Boolean(config.basic_auth?.username?.trim() || config.basic_auth?.password?.trim());
    case 'bearer_token':
      return Boolean(config.bearer_token?.token?.trim());
    case 'oauth2':
      return Boolean(
        config.oauth2?.client_id?.trim() ||
        config.oauth2?.client_secret?.trim() ||
        config.oauth2?.token_url?.trim() ||
        config.oauth2?.authorization_url?.trim() ||
        (config.oauth2?.scopes?.length ?? 0) > 0 ||
        Object.keys(config.oauth2?.auth_params ?? {}).some(
          key => key.trim() !== '' || String(config.oauth2?.auth_params?.[key] ?? '').trim() !== ''
        )
      );
    case 'none':
    default:
      return false;
  }
}

export default function AddConnectorAuthenticationStep({
  systemId,
  draftConfig,
  onDraftChange,
  onCompleted,
  onRequestClose,
}: AddConnectorAuthenticationStepProps) {
  const t = useTranslations('ConnectorAuthentication');
  const locale = useLocale();
  const router = useRouter();
  const updateSystem = useUpdateSystem();
  const setSystemStatus = useSetSystemStatus();
  const generateSummary = useGenerateSystemSummary();
  const resolvedWorkspaceId = getResolvedWorkspaceId('') ?? '';

  const [existingConfig, setExistingConfig] = useState<AuthConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const handleDraftChange = useCallback(
    (nextDraft: Partial<AuthConfig> | null) => {
      const inMemoryDraft = hasMeaningfulAuthDraft(nextDraft) ? nextDraft : null;
      onDraftChange(inMemoryDraft);
      updateFlowState({ hasAuthDraft: Boolean(inMemoryDraft) });
    },
    [onDraftChange]
  );

  useEffect(() => {
    const fetchExistingConfig = async () => {
      if (!systemId) return;

      try {
        const flowState = getFlowState();
        const isTemporary = flowState.isTemporary !== false && isTemporarySystemId(systemId);

        if (!isTemporary) {
          const response = await getAuthConfig(systemId);
          if (response.data && response.data.length > 0) {
            setExistingConfig(response.data[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch auth config:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchExistingConfig();
  }, [systemId]);

  const handleSave = async (config: AuthConfig) => {
    try {
      const currentFlowState = getFlowState();
      let finalSystemId = currentFlowState.systemId || systemId;
      const previewActions = (currentFlowState.previewActions || []) as GeneratedAction[];
      const connectorData = currentFlowState.connectorData;
      const persistedSystemId = isPersistedSystemId(finalSystemId) ? finalSystemId : '';

      if (!persistedSystemId) {
        if (!connectorData?.name) {
          throw new Error(t('errors.connectorDataMissing'));
        }

        const system = await createSystem(connectorData);
        finalSystemId = system.system_id;

        updateFlowState({
          systemId: finalSystemId,
          creationSystemId: finalSystemId,
          systemName: system.name,
          isTemporary: false,
        });

        await setSystemStatus(finalSystemId, 'inactive');
      } else {
        finalSystemId = persistedSystemId;

        if (connectorData?.name) {
          await updateSystem(finalSystemId, connectorData);
        }
      }

      if (!isPersistedSystemId(finalSystemId)) {
        throw new Error(t('errors.connectorDataMissing'));
      }

      if (previewActions.length > 0) {
        await batchCreateActions(finalSystemId, previewActions);
      }

      if (existingConfig?.authconfig_id) {
        await updateAuthConfig(finalSystemId, {
          ...config,
          authconfig_id: existingConfig.authconfig_id,
        });
      } else {
        await createAuthConfig({
          ...config,
          system_id: finalSystemId,
        });
      }

      // Authentication has been saved successfully, so the connector is now
      // safe to expose to consumers. The status update also refetches the
      // listing still mounted behind the modal, so it shows the saved
      // actions and auth.
      await setSystemStatus(finalSystemId, 'active');

      await revalidateOnboardingProgress(resolvedWorkspaceId);

      const connectorName = connectorData?.name || currentFlowState.systemName || '';

      clearCompletionScreenSeen(resolvedWorkspaceId, finalSystemId, connectorName);
      clearCompletionScreenDisplayState(resolvedWorkspaceId, finalSystemId, connectorName);
      armCompletionScreen(resolvedWorkspaceId, finalSystemId, connectorName);

      clearFlowState();

      const actionNames = previewActions.map(action => action.name);
      if (actionNames.length > 0) {
        generateSummary(finalSystemId).catch(err => {
          console.error('Failed to generate summary:', err);
        });
      }

      const completionParams = new URLSearchParams();
      completionParams.set('system_id', finalSystemId);
      if (connectorName) {
        completionParams.set('name', connectorName);
      }

      await pushWithMinimumDelay(
        router,
        buildWorkspaceAwarePath(`/${locale}/connectors/completed`, completionParams),
        {
          skipUnsavedCheck: true,
          showLoadingLayer: false,
        }
      );
      onCompleted();
    } catch (err) {
      console.error('Failed to save:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <PageLoadingState
        className="min-h-[400px]"
        label={t('loadingAuthentication')}
        spinnerClassName="border-0 border-b-2 border-purple-600"
      />
    );
  }

  return (
    <div className="mx-auto mt-8 w-full max-w-[850px]">
      <ManageAuthComponent
        systemId={systemId}
        existingConfig={existingConfig}
        draftConfig={draftConfig}
        onDraftChange={handleDraftChange}
        onSave={handleSave}
        onCancel={onRequestClose}
      />
    </div>
  );
}
